import React, { useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';
import { Badge } from '@dopomogai/ui';

export type FAQItem = {
  q: string;
  a: string;
  qKey?: string;
  aKey?: string;
};

export type FAQSectionProps = {
  title?: string;
  titleKey?: string;
  subtitle?: string; 
  items: FAQItem[];
  layout?: 'grid' | 'list';
  accent?: 'emerald' | 'violet' | 'blue';
  className?: string;
};

export const FAQSection: React.FC<FAQSectionProps> = ({
  title,
  titleKey,
  subtitle,
  items,
  layout = 'grid',
  accent = 'emerald',
  className,
}) => {
  const { t } = useTranslation('homepage');
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const resolvedTitle = titleKey ? t(titleKey) : (title || 'Frequently Asked Questions');

  const toggle = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  const accentColor = {
    emerald: { activeBorder: 'border-emerald-500', activeBg: 'bg-emerald-50', icon: 'text-emerald-600' },
    violet:  { activeBorder: 'border-violet-500',  activeBg: 'bg-violet-50',  icon: 'text-violet-600' },
    blue:    { activeBorder: 'border-blue-500',    activeBg: 'bg-blue-50',    icon: 'text-blue-600' },
  }[accent];

  return (
    <section className={clsx('bg-white py-20 md:py-28 border-t-2 border-slate-100', className)}>
      <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <Badge text="Knowledge Base" accentColor={accent} className="mb-4" />
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-4">
            {resolvedTitle}
          </h2>
          {subtitle && (
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </div>

        {/* The List */}
        <div className={clsx(
          'gap-4',
          layout === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 items-start' : 'flex flex-col'
        )}>
          {items.map((item, idx) => {
            const isOpen = openIndex === idx;
            const question = item.qKey ? t(item.qKey) : item.q;
            const answer = item.aKey ? t(item.aKey) : item.a;

            return (
              <div 
                key={idx}
                className={clsx(
                  "group rounded-xl border-2 transition-all duration-200 overflow-hidden",
                  isOpen 
                    ? clsx("border-black shadow-[4px_4px_0_0_#000]", accentColor.activeBg) 
                    : "border-slate-200 bg-white hover:border-slate-300"
                )}
              >
                <button
                  onClick={() => toggle(idx)}
                  className="w-full text-left px-6 py-5 flex items-start justify-between gap-4 focus:outline-none"
                  aria-expanded={isOpen}
                >
                  <span className={clsx(
                    "font-extrabold text-lg leading-snug",
                    isOpen ? "text-slate-900" : "text-slate-700 group-hover:text-slate-900"
                  )}>
                    {question}
                  </span>
                  
                  <span className={clsx(
                    "shrink-0 mt-0.5 transition-colors duration-200",
                    isOpen ? accentColor.icon : "text-slate-400 group-hover:text-slate-600"
                  )}>
                    {isOpen ? <Minus size={20} strokeWidth={3} /> : <Plus size={20} strokeWidth={3} />}
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                    >
                      <div className="px-6 pb-6">
                        <div className="pt-4 border-t border-slate-200/60 text-slate-600 font-medium leading-relaxed">
                          {answer}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Footer REMOVED to avoid duplication with Booking Section cta */}

      </div>
    </section>
  );
};

export default FAQSection;